import { isNil, last } from 'lodash';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { KnownCard } from './LastKnownCards';
import { CardMatch } from './cardMatch';
import { CardComparator } from './getCardComparator';

/** Where a card stands with one list. */
type Membership = 'in' | 'out' | 'unknown';

/**
 * Where a card stood with one list before an event. `unchanged` is a card past the loaded page
 * whose earlier version was never seen: it's assumed to have stood where it stands now, so the
 * count doesn't move. That's wrong only when the event moved it between groups.
 */
type MembershipBefore = Membership | 'unchanged';

interface ListUpdateProps {
  type: API.EntityEventType;
  cards: API.WorkflowEntity[];
  list: {
    entities?: (API.WorkflowEntity | null)[] | null;
    totalResults: number;
    hasMore: boolean;
  };
  // whether the list already has a change it couldn't settle, so it may be missing a card
  isStale: boolean;
  getMatch(card: API.WorkflowEntity, listedCard?: API.WorkflowEntity): CardMatch;
  // the card's version from before this event, if any list loaded it or an event reported it
  getPreviousCard(cardId: string): KnownCard | undefined;
  compareCards: CardComparator | undefined;
  // whether the list is sorted only on values no edit can change
  hasImmutableSort: boolean;
  // leave a card the list can't settle as it is, unmarked, for the event that will report it
  skipUnsettled?: boolean;
}

export interface ListUpdate {
  entities: API.WorkflowEntity[];
  totalResults: number;
  // whether the event may have changed which cards the list shows in a way it couldn't settle
  isStale: boolean;
  // whether the list shows the right cards but the event may have moved its count in a way it
  // couldn't settle
  isCountStale: boolean;
  // the cards the update applied - all of them, unless it skipped those it couldn't settle
  settledCardIds: string[];
}

/**
 * Applies a subscription event to one card list - a group, a tree level, or the whole collection -
 * without asking the server. For each card it works out whether the card was in the list before the
 * event and whether it is after, and moves the count by the difference. When either isn't known the
 * count is left alone. If the card could belong on the loaded page the list is marked stale, so the
 * customer is offered a refetch; otherwise only the count is, which a count query can settle.
 */
export function getListUpdate({
  type,
  cards,
  list,
  isStale,
  getMatch,
  getPreviousCard,
  compareCards,
  hasImmutableSort,
  skipUnsettled = false,
}: ListUpdateProps): ListUpdate {
  let entities = (list.entities ?? []).filter(notNil);
  let { totalResults } = list;
  let hasUnsettledCards = false;
  let hasUnsettledCount = false;
  const settledCardIds: string[] = [];

  // The end of the loaded page, as the server returned it.
  const lastLoadedCard = last(entities);
  const sortsBeforeLastLoaded = (card: API.WorkflowEntity) =>
    !isNil(compareCards) && !isNil(lastLoadedCard) && compareCards(card, lastLoadedCard) < 0;
  // A card past the end of the loaded page arrives with the page that holds it.
  const belongsOnLoadedPage = (card: API.WorkflowEntity) =>
    !list.hasMore || isNil(compareCards) || isNil(lastLoadedCard) || sortsBeforeLastLoaded(card);

  cards.forEach(card => {
    const isLoaded = entities.some(entity => entity.id === card.id);

    const before = getMembershipBefore({
      type,
      card,
      isLoaded,
      hasMore: list.hasMore,
      isStale,
      getMatch,
      getPreviousCard,
      // a card sorted ahead of the loaded page's end would already be loaded if it were in the
      // list, as long as no edit can have moved it there
      wouldBeLoaded: hasImmutableSort && sortsBeforeLastLoaded(card),
    });
    const after: Membership =
      type === 'WORKFLOW_ENTITY_DELETED'
        ? 'out'
        : toMembership(getMatch(card, isLoaded ? getPreviousCard(card.id)?.card : undefined));

    if (skipUnsettled && (before === 'unknown' || after === 'unknown')) {
      return;
    }
    settledCardIds.push(card.id);

    if (after === 'unknown' && (isLoaded || belongsOnLoadedPage(card))) {
      hasUnsettledCards = true;
    } else if (before === 'unknown' || after === 'unknown') {
      hasUnsettledCount = true;
    } else if (before !== 'unchanged') {
      totalResults = Math.max(0, totalResults + Number(after === 'in') - Number(before === 'in'));
    }

    if (isLoaded) {
      entities =
        after === 'out'
          ? entities.filter(entity => entity.id !== card.id)
          : entities.map(entity => (entity.id === card.id ? { ...card } : entity));
    } else if (after === 'in' && belongsOnLoadedPage(card)) {
      entities = [{ ...card }, ...entities];
    }
  });

  return {
    entities: isNil(compareCards) ? entities : [...entities].sort(compareCards),
    totalResults,
    isStale: hasUnsettledCards,
    isCountStale: hasUnsettledCount && !hasUnsettledCards,
    settledCardIds,
  };
}

interface MembershipBeforeProps extends Pick<
  ListUpdateProps,
  'type' | 'isStale' | 'getMatch' | 'getPreviousCard'
> {
  card: API.WorkflowEntity;
  isLoaded: boolean;
  hasMore: boolean;
  wouldBeLoaded: boolean;
}

function getMembershipBefore({
  type,
  card,
  isLoaded,
  hasMore,
  isStale,
  getMatch,
  getPreviousCard,
  wouldBeLoaded,
}: MembershipBeforeProps): MembershipBefore {
  if (isLoaded) {
    return 'in';
  }
  const previous = getPreviousCard(card.id);
  // a deleted card is in no list - including one this client deleted before the server said so
  if (!isNil(previous?.card.fields?.deletedAt)) {
    return 'out';
  }
  // A new or restored card is in no list yet, unless this client's own change already put it
  // there. A list loading it says nothing of that: it may have loaded after the change.
  if (type === 'WORKFLOW_ENTITY_CREATED' || type === 'WORKFLOW_ENTITY_RESTORED') {
    return previous?.isReported ? toMembership(getMatch(previous.card)) : 'out';
  }
  // A list that's stale may be missing a card, so neither of these can take a card's absence as
  // its not being in the list.
  if (!isStale && (!hasMore || wouldBeLoaded)) {
    return 'out';
  }
  // a deleted card arrives as it was, so it stood where it stands
  if (type === 'WORKFLOW_ENTITY_DELETED') {
    return toMembership(getMatch(card));
  }
  return isNil(previous) ? 'unchanged' : toMembership(getMatch(previous.card));
}

function toMembership(match: CardMatch): Membership {
  switch (match) {
    case 'match':
      return 'in';
    case 'noMatch':
      return 'out';
    case 'unknown':
      return 'unknown';
  }
}
